import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLocation, useNavigate } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Start', href: '#home' },
    { name: 'Unternehmen', href: '#about' },
    { name: 'Leistungen', href: '#services' },
    { name: 'Referenzen', href: '#references' },
    { name: 'Kontakt', href: '#contact' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    // Wenn wir nicht auf der Startseite sind, navigiere zuerst dorthin
    if (location.pathname !== '/') {
      navigate('/');
      // Kurze Verzögerung, bis die Startseite geladen ist
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo(0, 0);
        }
      }, 100);
    } else {
      // Wenn wir bereits auf der Startseite sind, scrolle direkt
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
         window.scrollTo(0, 0);
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white py-4 border-b border-slate-100'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo Area */}
          <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={handleLogoClick}>
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1774369849101-image.png" 
              alt="MK Netzbau GmbH Logo" 
              className={`transition-all duration-300 w-auto object-contain mix-blend-multiply ${isScrolled ? 'h-14' : 'h-16 md:h-20'}`}
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8 items-center">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-slate-700 hover:text-amber-500 font-semibold text-sm transition-colors duration-200 cursor-pointer"
              >
                {link.name}
              </a>
            ))}
            <button 
              onClick={(e) => handleNavClick(e, '#contact')}
              className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-md font-bold text-sm transition-colors duration-200 shadow-sm"
            >
              Anfrage stellen
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-800 hover:text-amber-500 focus:outline-none p-2"
            >
              <SafeIcon name={isMobileMenuOpen ? "X" : "Menu"} className="h-7 w-7" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white shadow-2xl border-t border-slate-100 absolute w-full left-0"
        >
          <div className="px-4 pt-2 pb-6 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block px-3 py-3 rounded-md text-base font-semibold text-slate-700 hover:text-amber-500 hover:bg-slate-50 cursor-pointer"
              >
                {link.name}
              </a>
            ))}
            <button 
              onClick={(e) => handleNavClick(e, '#contact')}
              className="w-full block px-3 py-3 mt-4 text-center rounded-md text-base font-bold bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
            >
              Anfrage stellen
            </button>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;